"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import FieldsFormatter from "@/components/organisms/admin/FieldsFormatter";
import { Button } from "@/components/atoms/Button";
import { validateFields } from "@/lib/utils";
import { toast } from "@/hooks/useToast";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import type { FormProps, Section } from "./types";
import {
  DEFAULT_MESSAGES,
  EXCLUDED_PAYLOAD_FIELDS,
  mergeRowWithAdditionalFields,
  buildPayloadWithJsonFields,
} from "./constants";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const buildPayload = (
  fields: Record<string, any>,
  userId?: number
): Record<string, any> => {
  const payload: Record<string, any> = { updated_by: userId };

  Object.entries(fields).forEach(([key, value]) => {
    if (!EXCLUDED_PAYLOAD_FIELDS.has(key)) {
      payload[key] = value;
    }
  });

  return payload;
};

const hasJsonFields = (config: Array<{ isJsonField?: boolean }>) =>
  (config || []).some((f) => f.isJsonField === true);

const showToast = {
  success: (message: string) => toast({ title: "Success", description: message }),
  error: (message: string) => toast({ title: "Error", description: message, variant: "destructive" }),
  validation: (title: string, description: string) => 
    toast({ title, description, variant: "destructive" }),
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const Form4 = ({ 
  selectedSection, 
  onSave, 
  variantDetails 
}: FormProps) => {
  const [componentFields, setComponentFields] = useState<Record<string, any>>({});
  const [componentFieldErrors, setComponentFieldErrors] = useState<Record<string, boolean>>({});
  const [componentFieldMessages, setComponentFieldMessages] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);

  const prevSectionRef = useRef<{ id?: string; componentId?: string; data?: any }>({});
  const dispatch = useAppDispatch();

  const constants = variantDetails?.constants;
  const currentUser = useAppSelector((state) => state.user?.user);
  
  const reduxState = useAppSelector((state) =>
    variantDetails?.selector ? variantDetails.selector(state.university) : null
  );

  const componentFieldsConfig = variantDetails?.componentFields || [];

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const hasChanged = 
      prevSectionRef.current?.id !== selectedSection.id ||
      prevSectionRef.current?.componentId !== selectedSection?.componentId ||
      prevSectionRef.current?.data !== selectedSection?.data;
    
    const isInitialMount = 
      prevSectionRef.current?.id === selectedSection.id && 
      Object.keys(componentFields).length === 0;

    if (!variantDetails || (!hasChanged && !isInitialMount)) return;

    prevSectionRef.current = {
      id: selectedSection.id,
      componentId: selectedSection?.componentId,
      data: selectedSection?.data,
    };

    const hasData = selectedSection?.data &&
      typeof selectedSection.data === "object" &&
      Object.keys(selectedSection.data).length > 0;

    const rawData = hasData ? selectedSection.data : (reduxState || {});
    const newFields = hasJsonFields(componentFieldsConfig)
      ? mergeRowWithAdditionalFields(rawData)
      : rawData;
    setComponentFields(newFields);

    if (hasData && variantDetails.setAction) {
      dispatch(variantDetails.setAction(newFields));
    }

    setComponentFieldErrors({});
    setComponentFieldMessages({});
  }, [
    selectedSection.id,
    selectedSection?.componentId,
    selectedSection?.data,
    reduxState,
    variantDetails,
    dispatch,
    componentFields,
  ]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleFieldsUpdate = useCallback(
    (updatedData: any) => {
      setComponentFields(updatedData);

      if (variantDetails?.setAction) {
        dispatch(
          variantDetails.setAction({
            ...reduxState,
            ...updatedData,
            id: reduxState?.id || "",
          })
        );
      }
    },
    [variantDetails, dispatch, reduxState]
  );

  const handleSave = async () => {
    if (!variantDetails || !constants) {
      showToast.error(constants?.CONFIG_NOT_FOUND_ERROR || DEFAULT_MESSAGES.CONFIG_NOT_FOUND);
      return;
    }

    const { errors, messages } = validateFields(componentFieldsConfig, componentFields);
    setComponentFieldErrors(errors);
    setComponentFieldMessages(messages);

    if (Object.keys(errors).length > 0) {
      showToast.validation(
        constants.VALIDATION_ERROR_TITLE || DEFAULT_MESSAGES.VALIDATION_ERROR_TITLE,
        constants.VALIDATION_ERROR_DESCRIPTION || DEFAULT_MESSAGES.VALIDATION_ERROR_DESCRIPTION
      );
      return;
    }

    setIsLoading(true);
    try {
      if (!variantDetails.updateMethod) {
        throw new Error(constants.UPDATE_METHOD_NOT_CONFIGURED_ERROR || DEFAULT_MESSAGES.UPDATE_METHOD_NOT_CONFIGURED);
      }

      const { id } = componentFields;
      if (!id) {
        throw new Error(DEFAULT_MESSAGES.ID_REQUIRED);
      }

      const payload = hasJsonFields(componentFieldsConfig)
        ? buildPayloadWithJsonFields(
            componentFields,
            componentFieldsConfig,
            EXCLUDED_PAYLOAD_FIELDS,
            currentUser?.id
          )
        : buildPayload(componentFields, currentUser?.id);
      await variantDetails.updateMethod(Number(id), payload);

      // Update Redux state
      if (variantDetails.setAction) {
        dispatch(variantDetails.setAction({ ...componentFields, id: String(id) }));
      }

      setComponentFields((prev) => ({ ...prev, id: String(id) }));

      const updatedSection: Section = {
        ...selectedSection,
        componentId: String(id),
        data: { ...componentFields, id: String(id) },
      };

      showToast.success(constants.SUCCESS_MESSAGE || DEFAULT_MESSAGES.SUCCESS);
      onSave(updatedSection);
    } catch (error: any) {
      console.error("Failed to save:", error);
      showToast.error(error.message || constants.ERROR_MESSAGE || DEFAULT_MESSAGES.ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (!variantDetails) return null;

  const saveButtonText = isLoading
    ? (constants?.SAVING_BUTTON_TEXT || DEFAULT_MESSAGES.SAVING_BUTTON)
    : (constants?.SAVE_BUTTON_TEXT || DEFAULT_MESSAGES.SAVE_BUTTON);

  return (
    <div>
      <div className="mb-6">
        <h3 className="font-semibold mb-2">{variantDetails.name}</h3>
        <p className="text-sm text-gray-500 mb-4">{variantDetails.description}</p>
      </div>

      {componentFieldsConfig.length > 0 && (
        <div className="border-t pt-6 mb-6">
          <h4 className="font-semibold mb-4 flex items-center gap-2">
            <span>📝</span> Content
          </h4>
          <FieldsFormatter
            data={componentFields}
            fields={componentFieldsConfig}
            updateParent={true}
            onChange={handleFieldsUpdate}
            validationErrors={componentFieldErrors}
            validationMessages={componentFieldMessages}
            className="p-0 bg-transparent"
          />
        </div>
      )}

      <div className="border-t pt-6 mb-6">
        <Button
          onClick={handleSave}
          classType="admin"
          className="w-full"
          disabled={isLoading}
        >
          {saveButtonText}
        </Button>
      </div>
    </div>
  );
};

export default Form4;

